import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { LuClipboardList, LuHospital, LuIdCard, LuPencil, LuReceipt, LuScale } from 'react-icons/lu';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import Modal from '../../components/ui/Modal';
import { EmptyNote, InfoItem, InfoSection } from '../../components/ui/InfoBlocks';
import { db } from '../../config/firebaseConfig';
import { useTranslation } from '../../i18n/LanguageContext';
import { isMirrorVisit } from '../../utils/hybridVisitMirror';
import { ProjectFlags, StatusBadge, useDueLabel } from './components/ProjectCell';
import {
  formatIsoDate,
  getFilterValueLabel,
  initialsOf,
  isCurrencyFixedFlag,
  isEmptyValue,
  projectStatusOf,
} from './projectColumns';

const display = (value) => (isEmptyValue(value) || String(value).trim() === '' ? '—' : String(value).trim());

/** "Proje Kartı": everything about a project at a glance (read only). */
const ProjectCardModal = ({ project, onClose, onEdit }) => {
  const { t, language, locale } = useTranslation();
  const dueLabel = useDueLabel();
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({ patients: 0, visits: 0 });
  // Liste verisindeki alanlar formatValue'dan geçtiği için employees dizisi
  // metne dönüşmüş olur; kart ham dokümanı Firestore'dan yeniden okur.
  const [rawEmployees, setRawEmployees] = useState(null);

  useEffect(() => {
    if (!project?.id) return undefined;
    let cancelled = false;
    const fetchCounts = async () => {
      setLoading(true);
      try {
        const [projectSnap, patientsSnap, visitsSnap] = await Promise.all([
          getDoc(doc(db, 'Projects', project.id)),
          getDocs(query(collection(db, 'Patients'), where('projectId', '==', project.id))),
          getDocs(query(collection(db, 'Visits'), where('projectId', '==', project.id))),
        ]);
        if (cancelled) return;
        const projectData = projectSnap.exists() ? projectSnap.data() : {};
        setRawEmployees(Array.isArray(projectData.employees) ? projectData.employees : []);
        // Hibrit projelerde unblinded çalışana düşen eş vizitler aynı hasta
        // vizitidir; proje vizit sayısında ikinci kez sayılmaz.
        const visitCount = visitsSnap.docs.filter((item) => !isMirrorVisit(item.data())).length;
        setCounts({ patients: patientsSnap.docs.length, visits: visitCount });
      } catch (error) {
        console.error('Error fetching project card data:', error);
        if (!cancelled) toast.error(t('projectManagement.card.loadError'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchCounts();
    return () => {
      cancelled = true;
    };
    // Reload for another project only, not when the language changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  // Proje dokümanındaki employees dizisi merkez/rol başına ayrı kayıt tutuyor
  const { activeWorkerCount, centerNames } = useMemo(() => {
    const employees = rawEmployees ?? (Array.isArray(project?.employees) ? project.employees : []);
    const activeIds = new Set();
    const centers = new Set();
    employees.forEach((emp) => {
      if (emp.status !== 'passive') {
        if (emp.id) activeIds.add(emp.id);
        if (emp.centerName) centers.add(emp.centerName);
      }
    });
    if (project?.center) centers.add(project.center);
    return { activeWorkerCount: activeIds.size, centerNames: [...centers] };
  }, [rawEmployees, project]);

  if (!project) return null;

  const option = (key) => (isEmptyValue(project[key]) ? '—' : getFilterValueLabel(t, key, project[key]));
  const date = (key) => display(formatIsoDate(project[key], locale));
  const currencyFixed = isCurrencyFixedFlag(project);
  const stats = [
    { key: 'workers', label: t('projectManagement.card.activeWorkers'), value: activeWorkerCount },
    { key: 'centers', label: t('projectManagement.card.centers'), value: centerNames.length },
    { key: 'patients', label: t('projectManagement.card.patients'), value: counts.patients },
    { key: 'visits', label: t('projectManagement.card.visits'), value: counts.visits },
  ];

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="form"
      title={t('projectManagement.card.title')}
      icon={(
        <GlassIcon gradient="tw-from-indigo-400 tw-to-violet-600 tw-shadow-violet-500/30" size="xs">
          <LuIdCard aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      closeLabel={t('common.close')}
      lang={language}
      bodyClassName="tw-p-0"
      footer={(
        <>
          <Button variant="ghost" onClick={() => onClose()}>{t('common.close')}</Button>
          {onEdit && (
            <Button variant="primary" onClick={onEdit}>
              <LuPencil aria-hidden="true" className="tw-h-4 tw-w-4" />
              {t('projectManagement.card.edit')}
            </Button>
          )}
        </>
      )}
    >
      <div className="md:tw-grid md:tw-grid-cols-[17rem_minmax(0,1fr)]">
        {/* Identity and summary */}
        <aside className="tw-space-y-4 tw-border-b tw-border-slate-200/80 tw-bg-slate-50/70 tw-p-5 md:tw-border-b-0 md:tw-border-r dark:tw-border-white/10 dark:tw-bg-white/[0.02]">
          <div className="tw-flex tw-items-center tw-gap-4 md:tw-flex-col md:tw-text-center">
            <GlassIcon gradient="tw-from-indigo-400 tw-to-violet-600 tw-shadow-violet-500/30" size="avatarXl" className="md:tw-h-20 md:tw-w-20 md:tw-text-2xl">
              {initialsOf(project.titleShort || project.customer)}
            </GlassIcon>
            <div className="tw-min-w-0">
              <h2 className="tw-break-words tw-text-lg tw-font-semibold tw-leading-tight tw-text-slate-900 dark:tw-text-white">{display(project.titleShort)}</h2>
              {display(project.titleLong) !== '—' && (
                <p className="tw-mt-1 tw-line-clamp-3 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400" title={project.titleLong}>{project.titleLong}</p>
              )}
              <div className="tw-mt-2.5 tw-flex tw-flex-wrap tw-gap-1.5 md:tw-justify-center">
                <StatusBadge project={project} />
                {!isEmptyValue(project.customer) && <Badge tone="neutral">{project.customer}</Badge>}
                {!isEmptyValue(project.projectPhase) && <Badge tone="info">{option('projectPhase')}</Badge>}
                <ProjectFlags project={project} />
              </div>
            </div>
          </div>

          <dl aria-label={t('projectManagement.card.summary')} className="tw-grid tw-grid-cols-2 tw-gap-2">
            {stats.map((stat) => (
              <div key={stat.key} className="tw-rounded-2xl tw-border tw-border-white/80 tw-bg-white/80 tw-px-3 tw-py-2.5 dark:tw-border-white/10 dark:tw-bg-white/[0.04]">
                <dt className="tw-truncate tw-text-[11px] tw-font-medium tw-text-slate-400 dark:tw-text-slate-500">{stat.label}</dt>
                <dd className="tw-mt-0.5 tw-text-xl tw-font-semibold tw-text-slate-900 tw-tabular-nums dark:tw-text-white">
                  {loading
                    ? <span aria-hidden="true" className="tw-block tw-h-6 tw-w-8 tw-animate-pulse tw-rounded-md tw-bg-slate-200/80 dark:tw-bg-white/10" />
                    : stat.value.toLocaleString(locale)}
                </dd>
              </div>
            ))}
          </dl>
        </aside>

        {/* Details */}
        <div className="tw-space-y-4 tw-p-3 sm:tw-p-4">
          <InfoSection icon={LuClipboardList} title={t('projectManagement.card.info')}>
            <InfoItem label={t('projectManagement.card.code')} value={display(project.titleShort)} copyable />
            <InfoItem label={t('projectManagement.columns.protocolNumber')} value={display(project.protocolNumber)} copyable />
            <InfoItem label={t('projectManagement.columns.customer')} value={display(project.customer)} />
            <InfoItem label={t('projectManagement.columns.projectStatus')} value={projectStatusOf(project) === 'Aktif' ? t('projectManagement.status.active') : t('projectManagement.status.inactive')} />
            <InfoItem label={t('projectManagement.columns.projectPhase')} value={option('projectPhase')} />
            <InfoItem label={t('projectManagement.columns.projectDesign')} value={option('projectDesign')} />
            <InfoItem label={t('projectManagement.columns.projectDepartment')} value={display(project.projectDepartment)} />
            <InfoItem label={t('projectManagement.columns.projectDate')} value={date('projectDate')} />
            <InfoItem label={t('projectManagement.columns.projectDueDate')} value={date('projectDueDate')} hint={dueLabel(project) || undefined} />
            <InfoItem label={t('projectManagement.columns.projectCentersType')} value={display(project.projectCentersType)} />
            <InfoItem label={t('projectManagement.columns.coordinatorAcademician')} value={display(project.coordinatorAcademician)} />
            <InfoItem label={t('projectManagement.card.name')} value={display(project.titleLong)} wide />
          </InfoSection>

          <InfoSection icon={LuScale} title={t('projectManagement.card.approval')}>
            <InfoItem label={t('projectManagement.columns.ethicsCommitteeDate')} value={date('ethicsCommitteeDate')} />
            <InfoItem label={t('projectManagement.columns.ethicsCommitteeNumber')} value={display(project.ethicsCommitteeNumber)} />
            <InfoItem label={t('projectManagement.columns.ethicsCommitteePlace')} value={display(project.ethicsCommitteePlace)} />
            <InfoItem label={t('projectManagement.columns.sbFirstApprovalDate')} value={date('sbFirstApprovalDate')} />
            <InfoItem label={t('projectManagement.columns.sbFirstApprovalNumber')} value={display(project.sbFirstApprovalNumber)} />
            <InfoItem label={t('projectManagement.columns.sbProjectNumber')} value={display(project.sbProjectNumber)} copyable />
          </InfoSection>

          <InfoSection icon={LuReceipt} title={t('projectManagement.card.finance')}>
            <InfoItem label={t('projectManagement.columns.firstInvoiceDate')} value={date('firstInvoiceDate')} />
            <InfoItem label={t('projectManagement.columns.paymentFrequency')} value={display(project.paymentFrequency)} />
            <InfoItem label={t('projectManagement.columns.isCurrencyFixed')} value={currencyFixed ? t('projectManagement.yes') : t('projectManagement.no')} />
            <InfoItem label={t('projectManagement.card.fixedRate')} value={currencyFixed ? display(project.fixedCurrencyRate) : '—'} />
          </InfoSection>

          <InfoSection icon={LuHospital} title={t('projectManagement.card.centersTitle', { count: centerNames.length })}>
            {centerNames.length === 0 ? (
              <EmptyNote>{t('projectManagement.card.noCenters')}</EmptyNote>
            ) : (
              <div className="tw-col-span-full tw-flex tw-flex-wrap tw-gap-1.5 tw-px-2.5 tw-py-1">
                {centerNames.map((name) => <Badge key={name} tone="accent">{name}</Badge>)}
              </div>
            )}
          </InfoSection>
        </div>
      </div>
    </Modal>
  );
};

ProjectCardModal.propTypes = {
  project: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onEdit: PropTypes.func,
};

export default ProjectCardModal;
